import { useState, useEffect } from 'react';
import { ArrowLeftRight, FileText, Truck, Check, AlertTriangle, Send, Eye, Plus, Loader2, Search } from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

interface InvoiceSwap {
  id: string;
  deal_id: string;
  purchase_order_id: string;
  vendor_invoice_number: string;
  vendor_invoice_date: string;
  vendor_invoice_amount: number;
  vendor_gstin: string;
  ve_invoice_id: string;
  eway_bill_number: string;
  truck_id: string;
  vendor_invoice_torn: boolean;
  vendor_invoice_torn_at: string | null;
  vendor_invoice_torn_by: string | null;
  sent_to_driver: boolean;
  sent_to_driver_at: string | null;
  sent_to_driver_method: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

export function InvoiceSwapPage() {
  const [swaps, setSwaps] = useState<InvoiceSwap[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({
    dealId: '', purchaseOrderId: '', vendorInvoiceNumber: '', vendorInvoiceDate: '',
    vendorInvoiceAmount: '', vendorGstin: '', veInvoiceId: '', eWayBillNumber: '',
    truckId: '', notes: '',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [swapRes, dealRes, poRes, invRes] = await Promise.all([
        api.get('/invoice-swaps').catch(() => ({ data: { data: [] } })),
        api.get('/veda-agro/deals').catch(() => ({ data: { data: [] } })),
        api.get('/purchase-orders?limit=100').catch(() => ({ data: { data: [] } })),
        api.get('/invoices?limit=100').catch(() => ({ data: { data: [] } })),
      ]);
      setSwaps(Array.isArray(swapRes.data.data) ? swapRes.data.data : []);
      setDeals(Array.isArray(dealRes.data.data) ? dealRes.data.data : []);
      setPos(Array.isArray(poRes.data?.data) ? poRes.data.data : (Array.isArray(poRes.data?.items) ? poRes.data.items : []));
      setInvoices(Array.isArray(invRes.data?.data) ? invRes.data.data : (Array.isArray(invRes.data?.items) ? invRes.data.items : []));
    } catch { /* ignore */ }
    setLoading(false);
  };

  const createSwap = async () => {
    if (!form.dealId || !form.vendorInvoiceNumber) return;
    setSaving(true);
    try {
      await api.post('/invoice-swaps', {
        dealId: form.dealId,
        purchaseOrderId: form.purchaseOrderId || null,
        vendorInvoiceNumber: form.vendorInvoiceNumber,
        vendorInvoiceDate: form.vendorInvoiceDate || null,
        vendorInvoiceAmount: form.vendorInvoiceAmount ? parseFloat(form.vendorInvoiceAmount) : null,
        vendorGstin: form.vendorGstin || null,
        veInvoiceId: form.veInvoiceId || null,
        eWayBillNumber: form.eWayBillNumber || null,
        truckId: form.truckId || null,
        notes: form.notes || null,
      });
      setShowModal(false);
      setForm({ dealId: '', purchaseOrderId: '', vendorInvoiceNumber: '', vendorInvoiceDate: '', vendorInvoiceAmount: '', vendorGstin: '', veInvoiceId: '', eWayBillNumber: '', truckId: '', notes: '' });
      loadData();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to create invoice swap');
    }
    setSaving(false);
  };

  const markTorn = async (id: string) => {
    try {
      await api.patch(`/invoice-swaps/${id}/torn`);
      loadData();
    } catch { alert('Failed to mark as torn'); }
  };

  const markSentToDriver = async (id: string, method: string) => {
    try {
      await api.patch(`/invoice-swaps/${id}/sent-to-driver`, { method });
      loadData();
    } catch { alert('Failed to mark as sent'); }
  };

  const getStatusBadge = (s: InvoiceSwap) => {
    if (s.status === 'COMPLETE') return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Complete</span>;
    if (s.sent_to_driver && s.vendor_invoice_torn) return <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">Vendor Torn + Sent</span>;
    if (s.sent_to_driver) return <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">Sent to Driver</span>;
    if (s.vendor_invoice_torn) return <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">Vendor Invoice Torn</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">Pending</span>;
  };

  const filtered = swaps.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.vendor_invoice_number?.toLowerCase().includes(q) || s.eway_bill_number?.toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total: swaps.length,
    pending: swaps.filter(s => s.status === 'PENDING').length,
    sentToDriver: swaps.filter(s => s.sent_to_driver).length,
    tornCount: swaps.filter(s => s.vendor_invoice_torn).length,
    complete: swaps.filter(s => s.status === 'COMPLETE').length,
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin h-8 w-8 text-blue-600" /></div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ArrowLeftRight className="h-6 w-6 text-blue-600" />
            Invoice Swap — Trading Document Flow
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Vendor purchase invoice → Veda Agro sales invoice + e-Way Bill → Truck driver carries only Veda Agro docs
          </p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New Invoice Swap
        </button>
      </div>

      {/* Workflow Explanation Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">Trading Invoice Swap Flow:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Record vendor&apos;s purchase invoice (your procurement cost proof)</li>
              <li>Create Veda Agro&apos;s sales invoice + e-Way Bill in AURA</li>
              <li>Send Veda Agro docs to truck driver (WhatsApp/email)</li>
              <li>Driver tears vendor invoice — record the tear for audit trail</li>
              <li>Driver delivers with only Veda Agro docs to buyer</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: 'Total Swaps', value: stats.total, color: 'blue' },
          { label: 'Pending', value: stats.pending, color: 'gray' },
          { label: 'Sent to Driver', value: stats.sentToDriver, color: 'indigo' },
          { label: 'Vendor Torn', value: stats.tornCount, color: 'yellow' },
          { label: 'Complete', value: stats.complete, color: 'green' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg border p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className="text-2xl font-bold mt-1">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by vendor invoice # or e-Way Bill..." className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="all">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="SENT_TO_DRIVER">Sent to Driver</option>
          <option value="VENDOR_INVOICE_TORN">Vendor Torn</option>
          <option value="COMPLETE">Complete</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Vendor Invoice</th>
              <th className="text-left px-4 py-3 font-medium">Amount</th>
              <th className="text-left px-4 py-3 font-medium">VA Invoice</th>
              <th className="text-left px-4 py-3 font-medium">e-Way Bill</th>
              <th className="text-left px-4 py-3 font-medium">Truck</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No invoice swaps yet. Create one to start the trading document flow.</td></tr>
            ) : filtered.map(s => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium">{s.vendor_invoice_number}</div>
                  <div className="text-xs text-gray-500">{s.vendor_gstin || 'No GSTIN'}</div>
                  {s.vendor_invoice_torn && <div className="text-xs text-red-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" /> Torn {s.vendor_invoice_torn_at ? formatDate(s.vendor_invoice_torn_at) : ''}</div>}
                </td>
                <td className="px-4 py-3 font-mono">{s.vendor_invoice_amount ? formatINR(s.vendor_invoice_amount) : '—'}</td>
                <td className="px-4 py-3">
                  {s.ve_invoice_id ? <span className="text-blue-600 text-xs">Linked</span> : <span className="text-gray-400 text-xs">Not yet</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{s.eway_bill_number || '—'}</td>
                <td className="px-4 py-3">
                  {s.sent_to_driver ? (
                    <div className="flex items-center gap-1 text-green-600 text-xs"><Check className="h-3 w-3" /> via {s.sent_to_driver_method}</div>
                  ) : <span className="text-gray-400 text-xs">Not sent</span>}
                </td>
                <td className="px-4 py-3">{getStatusBadge(s)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {!s.sent_to_driver && (
                      <div className="flex gap-1">
                        <button onClick={() => markSentToDriver(s.id, 'whatsapp')} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200" title="Send to driver via WhatsApp">
                          <Send className="h-3 w-3" />
                        </button>
                        <button onClick={() => markSentToDriver(s.id, 'email')} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200" title="Send via email">
                          <FileText className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {!s.vendor_invoice_torn && (
                      <button onClick={() => markTorn(s.id)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200" title="Mark vendor invoice as torn">
                        Tear
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-lg font-bold">Record Invoice Swap</h2>
              <p className="text-sm text-gray-500">Record vendor&apos;s purchase invoice and link to Veda Agro&apos;s sales docs</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium">Deal *</label>
                <select value={form.dealId} onChange={e => setForm({ ...form, dealId: e.target.value })} className="w-full border rounded-lg px-3 py-2 mt-1 text-sm">
                  <option value="">Select deal...</option>
                  {deals.map((d: any) => <option key={d.id} value={d.id}>{d.commodity} — {d.buyerName} ({d.salesOrder?.number || 'N/A'})</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Purchase Order</label>
                <select value={form.purchaseOrderId} onChange={e => setForm({ ...form, purchaseOrderId: e.target.value })} className="w-full border rounded-lg px-3 py-2 mt-1 text-sm">
                  <option value="">Select PO (optional)...</option>
                  {pos.map((p: any) => <option key={p.id} value={p.id}>{p.number} — {p.vendor?.name || 'N/A'}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Vendor Invoice # *</label>
                  <input value={form.vendorInvoiceNumber} onChange={e => setForm({ ...form, vendorInvoiceNumber: e.target.value })} placeholder="INV-2026-001" className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium">Invoice Date</label>
                  <input type="date" value={form.vendorInvoiceDate} onChange={e => setForm({ ...form, vendorInvoiceDate: e.target.value })} className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Invoice Amount (₹)</label>
                  <input type="number" value={form.vendorInvoiceAmount} onChange={e => setForm({ ...form, vendorInvoiceAmount: e.target.value })} placeholder="0" className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium">Vendor GSTIN</label>
                  <input value={form.vendorGstin} onChange={e => setForm({ ...form, vendorGstin: e.target.value })} placeholder="22AAAAA0000A1Z5" className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Veda Agro Sales Invoice (link)</label>
                <select value={form.veInvoiceId} onChange={e => setForm({ ...form, veInvoiceId: e.target.value })} className="w-full border rounded-lg px-3 py-2 mt-1 text-sm">
                  <option value="">Select your sales invoice...</option>
                  {invoices.map((i: any) => <option key={i.id} value={i.id}>{i.number} — {formatINR(i.total)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">e-Way Bill Number</label>
                <input value={form.eWayBillNumber} onChange={e => setForm({ ...form, eWayBillNumber: e.target.value })} placeholder="EWB-2026-XXXXX" className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Special instructions for driver..." rows={2} className="w-full border rounded-lg px-3 py-2 mt-1 text-sm" />
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
              <button onClick={createSwap} disabled={saving || !form.dealId || !form.vendorInvoiceNumber} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Record Invoice Swap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
